import { Download, Info, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getAuthState } from '@/stores/auth.store';

interface DownloadReportModalProps {
  open: boolean;
  assessmentId: string;
  onSkip: () => void;
}

/**
 * Download Report Modal - shown after initial screening completes.
 * Allows users to download a basic PDF report before being offered the full report.
 */
export function DownloadReportModal({ open, assessmentId, onSkip }: DownloadReportModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const { accessToken } = getAuthState();

      const headers: HeadersInit = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetch(`/api/risk-assessments/${assessmentId}/basic-report/pdf`, {
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      a.download = filenameMatch ? filenameMatch[1] : 'screening-summary.pdf';

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[600px]" hideCloseButton>
        <DialogHeader className="pt-4 text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Download Basic Report
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Description */}
          <p className="text-center text-[14px] leading-relaxed text-gray-600">
            Download a basic screening summary as a PDF. This is included with your initial
            screening.
          </p>

          {/* Info about available report upgrades */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 flex-shrink-0 text-blue-600" />
              <div className="text-[13px] text-gray-700">
                <p className="font-medium text-[#051923]">Report upgrades available</p>
                <ul className="mt-2 space-y-2">
                  <li>
                    <span className="font-medium">Match Report (£7.50)</span> — Detailed findings
                    with full sanctions and adverse media matches
                  </li>
                  <li>
                    <span className="font-medium">Enhanced Due Diligence (+£15)</span> — AI-powered
                    analysis, full article extraction (available after Match Report)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isDownloading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="mr-2 h-5 w-5" />
                Download PDF
              </>
            )}
          </Button>
          <button
            type="button"
            onClick={onSkip}
            disabled={isDownloading}
            className="py-2 text-[16px] font-medium text-gray-600 underline hover:text-gray-800"
          >
            Continue to report options
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
